import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { trackEvent } from '@xingternal/hops-tracking';
import { IconFilter } from '@xingternal/icons';
import { Headline, Hero } from '@xingternal/typography';

import { useBreakpoints, useLoginStates } from '@/hooks';

import { useSearchQuery } from '../../../contexts';
import { useSearch } from '../../../hooks/use-search';
import { AlertButton } from '../../alert-button';
import { LimitedJobCount } from '../../limited-job-count';
import { ResultsHeaderMobileContent } from './results-header-mobile-content';

import * as S from './results-header.styles';

type Props = {
  hideSorting?: boolean;
};

export const ResultsHeader: React.VFC<Props> = ({ hideSorting = false }) => {
  const { filterCount } = useSearchQuery();
  const { data } = useSearch();
  const { isMobile, isDesktop, isTablet } = useBreakpoints();
  const { isLoggedIn } = useLoginStates();
  const [isFiltersModalShown, setIsFiltersModalShown] = useState(false);

  const resultsTotal = data?.jobSearchByQuery?.total ?? 0;

  const onOpenFiltersModal = (): void => {
    setIsFiltersModalShown(true);
    trackEvent('PropTrackAction', { PropTrackAction: 'jobs_search_filter_setting_open' });
  };

  const onCloseFiltersModal = (): void => {
    setIsFiltersModalShown(false);
  };

  const onSaveFilters = (): void => {
    onCloseFiltersModal();
  };

  const Title = (
    <FormattedMessage
      id={
        resultsTotal === 0
          ? 'JOBS_SEARCH_EMPTY_STATE_NO_RESULTS_TITLE'
          : resultsTotal === 1
          ? 'SE_NUMBER_OF_JOBS_RESULTS_SINGULAR'
          : 'SE_NUMBER_OF_JOBS_RESULTS'
      }
      values={{ total: <LimitedJobCount count={resultsTotal} /> }}
    />
  );

  return (
    <>
      <S.Container>
        {isDesktop ? (
          <Hero as="h2" size="small" noMargin data-cy="search-results-header">
            {Title}
          </Hero>
        ) : (
          <Headline size="xxlarge" noMargin data-cy="search-results-header">
            {Title}
          </Headline>
        )}

        {isMobile ? (
          <S.TextButton icon={IconFilter} iconAfter onClick={onOpenFiltersModal}>
            <FormattedMessage id="JOBS_SEARCH_MORE_FILTERS_MOBILE" /> {filterCount > 0 ? `(${filterCount})` : null}
          </S.TextButton>
        ) : (
          <S.Buttons>
            <AlertButton
              size={isTablet ? 'small' : 'medium'}
              propActionOrigin={isLoggedIn ? 'jobs_bell_link' : 'jobs_serp_hyperlink'}
            />
            {hideSorting ? null : <S.SortingButton />}
          </S.Buttons>
        )}
      </S.Container>

      {isMobile ? (
        <ResultsHeaderMobileContent
          showFiltersDialog={isFiltersModalShown}
          onClose={onCloseFiltersModal}
          onSave={onSaveFilters}
        />
      ) : null}
    </>
  );
};
